import React from 'react';
import AudioPrompt from './AudioPrompt';
import DrawingCanvas from './DrawingCanvas';
import ImageSelectQuestion from './ImageSelectQuestion';
import TapTargetQuestion from './TapTargetQuestion';
import DragDropQuestion from './DragDropQuestion';

export default function QuestionRenderer({ question, value, onChange, disabled = false }) {
  const { question_type, prompt, help_text, config, required } = question;

  // Check if this is a student-facing question type
  const isStudentFacing = ['image_select', 'tap_target', 'drawing', 'drag_drop', 'audio_choice'].includes(question_type);

  const renderQuestion = () => {
    switch (question_type) {
      case 'observation':
      case 'scale':
        return renderScaleQuestion();
      case 'multiple_choice':
        return renderMultipleChoice();
      case 'multi_select':
        return renderMultiSelect();
      case 'yes_no':
        return renderYesNo();
      case 'checklist':
        return renderChecklist();
      case 'text':
        return renderText();
      case 'number':
        return renderNumber();
      case 'image_select':
        return <ImageSelectQuestion question={question} value={value} onChange={onChange} disabled={disabled} />;
      case 'tap_target':
        return <TapTargetQuestion question={question} value={value} onChange={onChange} disabled={disabled} />;
      case 'drawing':
        return renderDrawing();
      case 'drag_drop':
        return <DragDropQuestion question={question} value={value} onChange={onChange} disabled={disabled} />;
      case 'audio_choice':
        return renderAudioChoice();
      default:
        return <p className="text-red-500">Unknown question type: {question_type}</p>;
    }
  };

  const renderScaleQuestion = () => {
    const options = config?.options || [];
    return (
      <div className="space-y-2">
        {options.map((option, index) => (
          <label
            key={index}
            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              value === option.value
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'cursor-not-allowed opacity-75' : ''}`}
          >
            <input
              type="radio"
              name={`question-${question.id}`}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              disabled={disabled}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderMultipleChoice = () => {
    const options = config?.options || [];
    return (
      <div className="space-y-2">
        {options.map((option, index) => (
          <label
            key={index}
            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              value === option.value
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'cursor-not-allowed opacity-75' : ''}`}
          >
            <input
              type="radio"
              name={`question-${question.id}`}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              disabled={disabled}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderMultiSelect = () => {
    const options = config?.options || [];
    const selectedValues = Array.isArray(value) ? value : [];

    const handleToggle = (optionValue) => {
      if (selectedValues.includes(optionValue)) {
        onChange(selectedValues.filter((v) => v !== optionValue));
      } else {
        onChange([...selectedValues, optionValue]);
      }
    };

    return (
      <div className="space-y-2">
        {options.map((option, index) => (
          <label
            key={index}
            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              selectedValues.includes(option.value)
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'cursor-not-allowed opacity-75' : ''}`}
          >
            <input
              type="checkbox"
              checked={selectedValues.includes(option.value)}
              onChange={() => handleToggle(option.value)}
              disabled={disabled}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderYesNo = () => {
    const options = [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];
    return (
      <div className="flex space-x-4">
        {options.map((option) => (
          <label
            key={option.label}
            className={`flex-1 flex items-center justify-center p-4 border rounded-lg cursor-pointer transition-colors ${
              value === option.value
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'cursor-not-allowed opacity-75' : ''}`}
          >
            <input
              type="radio"
              name={`question-${question.id}`}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              disabled={disabled}
              className="sr-only"
            />
            <span className="text-lg font-medium text-gray-900">{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderChecklist = () => {
    const items = config?.items || [];
    const checkedItems = typeof value === 'object' ? value : {};

    const handleCheck = (itemKey, checked) => {
      onChange({ ...checkedItems, [itemKey]: checked });
    };

    return (
      <div className="space-y-2">
        {items.map((item, index) => (
          <label
            key={index}
            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              checkedItems[item.key]
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'cursor-not-allowed opacity-75' : ''}`}
          >
            <input
              type="checkbox"
              checked={checkedItems[item.key] || false}
              onChange={(e) => handleCheck(item.key, e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">{item.label}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderText = () => {
    const { min_length, max_length, placeholder } = config || {};
    return (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder || 'Enter your answer...'}
        minLength={min_length}
        maxLength={max_length}
        rows={4}
        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
      />
    );
  };

  const renderNumber = () => {
    const { min, max, step, unit } = config || {};
    return (
      <div className="flex items-center space-x-2">
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          disabled={disabled}
          min={min}
          max={max}
          step={step || 1}
          className="w-32 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        {unit && <span className="text-sm text-gray-500">{unit}</span>}
      </div>
    );
  };

  const renderDrawing = () => {
    const audioPrompt = config?.audio_prompt;
    const guideImage = config?.guide_image;
    const canvasSize = config?.canvas_size || { width: 300, height: 300 };

    return (
      <div className="space-y-4">
        {/* Audio prompt for young students */}
        {audioPrompt && <AudioPrompt text={audioPrompt} autoPlay />}

        {/* Drawing canvas */}
        <DrawingCanvas
          width={canvasSize.width}
          height={canvasSize.height}
          guideImage={guideImage}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />

        {/* Help text for what to draw */}
        {config?.expected && (
          <p className="text-sm text-gray-500">
            Draw: <span className="font-medium">{config.expected}</span>
          </p>
        )}
      </div>
    );
  };

  const renderAudioChoice = () => {
    const audioPrompt = config?.audio_prompt;
    const options = config?.options || [];

    return (
      <div className="space-y-4">
        {/* Audio prompt for the question */}
        {audioPrompt && <AudioPrompt text={audioPrompt} autoPlay />}

        {/* Choice buttons - larger for young students */}
        <div className="grid grid-cols-2 gap-4">
          {options.map((option, index) => {
            const isSelected = value === option.value;
            return (
              <button
                key={index}
                type="button"
                onClick={() => !disabled && onChange(option.value)}
                disabled={disabled}
                className={`relative p-4 rounded-xl border-4 transition-all ${
                  isSelected
                    ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                } ${disabled ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
              >
                <div className="flex flex-col items-center space-y-2">
                  {option.image ? (
                    <img
                      src={option.image}
                      alt={option.label}
                      className="h-16 w-16 object-contain"
                    />
                  ) : (
                    <span className="text-2xl font-bold text-gray-700">{option.label}</span>
                  )}
                  {option.audio_label && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const utterance = new SpeechSynthesisUtterance(option.audio_label);
                        window.speechSynthesis.speak(utterance);
                      }}
                      className="text-indigo-600 text-sm hover:underline"
                    >
                      Listen
                    </button>
                  )}
                </div>

                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Show text prompt for non-student-facing questions */}
      {!isStudentFacing && (
        <div>
          <h3 className="text-md font-medium text-gray-900">
            {prompt}
            {required && <span className="text-red-500 ml-1">*</span>}
          </h3>
          {help_text && <p className="mt-1 text-sm text-gray-500">{help_text}</p>}
        </div>
      )}
      {/* Student-facing questions have their own audio prompts */}
      {isStudentFacing && help_text && (
        <p className="text-sm text-gray-500">{help_text}</p>
      )}
      {renderQuestion()}
    </div>
  );
}
